import {
  Mail, UserCheck, Crown, Activity, Percent,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Send } from "lucide-react";

const ReferralDashboard = ({ referralData }) => (
  <Card className="card-surface" data-testid="referral-dashboard">
    <CardHeader className="pb-3">
      <CardTitle className="text-base flex items-center gap-2">
        <Send className="w-4 h-4 text-[var(--t-primary)]" />
        Referral Tracking
      </CardTitle>
      <CardDescription>Monitor invite-a-friend activity and conversion rates</CardDescription>
    </CardHeader>
    <CardContent>
      {referralData ? (
        <div className="space-y-5">
          <div className="grid grid-cols-3 gap-3" data-testid="referral-metrics">
            {[
              { label: "Invites Sent", value: referralData.total_sent, icon: Mail, color: "#1a56db" },
              { label: "Converted", value: referralData.total_converted, icon: UserCheck, color: "#059669" },
              { label: "Conversion Rate", value: `${referralData.conversion_rate}%`, icon: Percent, color: "#7C3AED" },
            ].map((m) => (
              <div key={m.label} className="p-4 rounded-lg border border-[var(--t-border)] bg-[var(--t-bg)]">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: m.color + "15" }}>
                    <m.icon className="w-3.5 h-3.5" style={{ color: m.color }} />
                  </div>
                </div>
                <p className="text-2xl font-bold text-[var(--t-text)]">{m.value}</p>
                <p className="text-[11px] text-[var(--t-text-muted)]">{m.label}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="border border-[var(--t-border)] rounded-lg overflow-hidden">
              <div className="px-4 py-2.5 bg-[var(--t-bg)] border-b border-[var(--t-border)]">
                <p className="text-xs font-semibold text-[var(--t-text)] flex items-center gap-1.5">
                  <Crown className="w-3 h-3 text-amber-500" /> Top Referrers
                </p>
              </div>
              {referralData.top_senders.length === 0 ? (
                <div className="p-6 text-center text-sm text-[var(--t-text-muted)]">No referrals yet</div>
              ) : (
                <table className="w-full text-sm" data-testid="top-senders-table">
                  <thead>
                    <tr className="text-left text-[var(--t-text-muted)] border-b border-[var(--t-border)]">
                      <th className="px-4 py-2 font-medium text-xs">Sender</th>
                      <th className="px-4 py-2 font-medium text-xs text-center">Sent</th>
                      <th className="px-4 py-2 font-medium text-xs text-center">Converted</th>
                    </tr>
                  </thead>
                  <tbody>
                    {referralData.top_senders.map((s) => (
                      <tr key={s.sender_email} className="border-b border-[var(--t-border)] last:border-0 hover:bg-[var(--t-bg)]">
                        <td className="px-4 py-2">
                          <div className="font-medium text-[var(--t-text)] text-xs">{s.sender_name}</div>
                          <div className="text-[10px] text-[var(--t-text-muted)]">{s.sender_email}</div>
                        </td>
                        <td className="px-4 py-2 text-center">
                          <Badge variant="outline" className="text-[10px]">{s.count}</Badge>
                        </td>
                        <td className="px-4 py-2 text-center">
                          <Badge className={`text-[10px] ${s.converted > 0 ? "bg-green-100 text-green-700 border-green-200" : "bg-gray-100 text-gray-500 border-gray-200"}`}>
                            {s.converted}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="border border-[var(--t-border)] rounded-lg overflow-hidden">
              <div className="px-4 py-2.5 bg-[var(--t-bg)] border-b border-[var(--t-border)]">
                <p className="text-xs font-semibold text-[var(--t-text)] flex items-center gap-1.5">
                  <Activity className="w-3 h-3 text-blue-500" /> Recent Invites
                </p>
              </div>
              {referralData.recent_referrals.length === 0 ? (
                <div className="p-6 text-center text-sm text-[var(--t-text-muted)]">No referrals yet</div>
              ) : (
                <div className="max-h-[280px] overflow-y-auto" data-testid="recent-referrals-list">
                  {referralData.recent_referrals.map((r) => (
                    <div key={`${r.sender_email}-${r.recipient_email}-${r.sent_at}`} className="flex items-center justify-between px-4 py-2.5 border-b border-[var(--t-border)] last:border-0 hover:bg-[var(--t-bg)]">
                      <div className="min-w-0">
                        <div className="text-xs text-[var(--t-text)] truncate">
                          <span className="font-medium">{r.sender_name}</span>
                          <span className="text-[var(--t-text-muted)]"> invited </span>
                          <span className="font-medium">{r.recipient_name || r.recipient_email}</span>
                        </div>
                        <div className="text-[10px] text-[var(--t-text-muted)]">
                          {r.recipient_email} &middot; {new Date(r.sent_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                        </div>
                      </div>
                      <Badge className={`text-[10px] ml-2 shrink-0 ${r.converted ? "bg-green-100 text-green-700 border-green-200" : "bg-amber-50 text-amber-600 border-amber-200"}`}>
                        {r.converted ? "Signed Up" : "Pending"}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="py-8 text-center text-sm text-[var(--t-text-muted)]">Loading referral data...</div>
      )}
    </CardContent>
  </Card>
);

export default ReferralDashboard;
